import { OrderAllocatedExpense, PaymentTaskFixedAsset } from '@/hooks/api/models';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError } from '@/lib/toast';
import { useParams } from 'react-router-dom';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { useUpdateOrderAllocatedExpense } from '@/hooks/api/1194_update_order_allocated_expense';
import { useInvalidateOrderAllocatedExpenses } from '@/hooks/api/1192_order_allocated_expenses';
import { SalesOrderOutlineLogic } from '@/hooks/api/models/logics';

type FixedAssetFormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  id: number;
  amount: number;
};

export const AllocatedExpenseRow = ({
  d,
  setDelTarget,
}: {
  d: OrderAllocatedExpense;
  setDelTarget: Dispatch<SetStateAction<OrderAllocatedExpense | undefined>>;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task, setPaymentTask } = useCurrentPaymentTask();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, isSuccess, error, isPending } = useUpdateOrderAllocatedExpense();
  const { invalidate } = useInvalidateOrderAllocatedExpenses({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FixedAssetFormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      id: d.id,
      amount: d.amount,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FixedAssetFormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (isSuccess && data) {
      setEditing(false);
      invalidate();
    }
  }, [isSuccess]);

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
            <i className="fas fa-project-diagram text-white"></i>
          </div>
          <div className="ms-2">
            <div className="font-weight-bold px-0 text-sm text-dark mb-1">
              {SalesOrderOutlineLogic.toLabel(d.related_order.sales_order_outline)}
            </div>
          </div>
        </div>
      </td>
      {editing && (
        <>
          <td className="text-end text-dark text-xs">
            <div className="input-group">
              <Controller
                render={({ field: { onChange, value, ...field } }) => (
                  <InputPrice onChange={onChange} value={value} error={errors.amount} />
                )}
                control={control}
                name="amount"
                rules={{ required: true }}
              />
            </div>
          </td>
          <td className="text-end">
            <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing(false)}>
              <i className="fas fa-undo"></i>
            </button>
            <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
              <i className="fas fa-check-circle"></i>
            </button>
          </td>
        </>
      )}
      {!editing && (
        <>
          <td className="text-end text-dark text-xs">¥{d.amount.toLocaleString()}</td>
          <td className="text-end">
            <button className="btn btn-icon-only bg-gradient-success me-1" onClick={() => setEditing(true)}>
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-icon-only bg-gradient-danger"
              data-bs-toggle="modal"
              data-bs-target={`#delConfirm`}
              onClick={() => setDelTarget((_) => d)}
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </td>
        </>
      )}
    </tr>
  );
};
